import React, { createContext, useContext, useState, useEffect } from "react";
import * as boardApi from "../api/boardApi";
import { ToastContext } from "./toast-context";

const DEFAULT_COLUMN_NAMES = ["Todo", "Doing", "Done"];

const tempId = () => `temp-${crypto.randomUUID()}`;

const buildColumns = (names) => names.map((name) => ({ id: tempId(), name, tasks: [] }));

// Converts the API's wire shape (subtasks: [...]) into the shape the rest
// of the app already expects (subTasks: [...]), so no consuming component
// needs to know about the backend's field naming.
const normalizeBoard = (board) => ({
  id: board.id,
  title: board.title,
  columns: board.columns.map((col) => ({
    id: col.id,
    name: col.name,
    tasks: col.tasks.map((task) => ({
      id: task.id,
      title: task.title,
      description: task.description,
      subTasks: task.subtasks.map((st) => ({
        id: st.id,
        title: st.title,
        isCompleted: st.isCompleted,
      })),
    })),
  })),
});

export const BoardContext = createContext({
  boards: [],
  activeBoardIndex: 0,
  sidebarOpen: false,
  isLoading: true,
  error: null,
  setSidebarOpen: () => {},
  createNewBoard: () => {},
  selectBoard: () => {},
  editBoard: () => {},
  deleteBoard: () => {},
  createNewColumn: () => {},
  deleteColumn: () => {},
  reorderColumns: () => {},
  addNewTask: () => {},
  editTask: () => {},
  deleteTask: () => {},
  toggleSubtask: () => {},
  moveTask: () => {},
});

BoardContext.displayName = "BoardContext";

export const BoardProvider = ({ children }) => {
  const [boards, setBoards] = useState([]);
  const [activeBoardIndex, setActiveBoardIndex] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showToast } = useContext(ToastContext);

  useEffect(() => {
    let cancelled = false;
    boardApi
      .fetchBoards()
      .then((data) => {
        if (cancelled) return;
        setBoards(data.map(normalizeBoard));
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-fetches everything from the server after a failed action, so the
  // optimistic update we already applied gets corrected back to truth. Only
  // sets the blocking `error` state if the server is genuinely unreachable
  // — a single failed action shows a toast instead (see each action below).
  const resync = async () => {
    try {
      const data = await boardApi.fetchBoards();
      setBoards(data.map(normalizeBoard));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const replaceBoard = (board) => {
    const normalized = normalizeBoard(board);
    setBoards((prev) => prev.map((b) => (b.id === board.id ? normalized : b)));
  };

  const createNewBoard = async (title, columnNames = DEFAULT_COLUMN_NAMES) => {
    const names = columnNames.length ? columnNames : DEFAULT_COLUMN_NAMES;
    const optimisticId = tempId();
    const newIndex = boards.length;

    setBoards((prev) => [...prev, { id: optimisticId, title, columns: buildColumns(names) }]);
    setActiveBoardIndex(newIndex);

    try {
      const board = await boardApi.createBoard(title, names);
      setBoards((prev) => prev.map((b) => (b.id === optimisticId ? normalizeBoard(board) : b)));
    } catch (err) {
      showToast(`Couldn't create the board: ${err.message}`);
      setBoards((prev) => prev.filter((b) => b.id !== optimisticId));
      await resync();
    }
  };

  const selectBoard = (index) => setActiveBoardIndex(index);

  // Reconciles the board's columns against a new list of column names,
  // keeping tasks for columns whose name survives and dropping the rest.
  const editBoard = async (boardId, title, columnNames) => {
    setBoards((prev) =>
      prev.map((board) => {
        if (board.id !== boardId) return board;
        const columns = columnNames
          .filter((name) => name.trim())
          .map((name) => {
            const existing = board.columns.find((col) => col.name === name);
            return existing || { id: tempId(), name, tasks: [] };
          });
        return { ...board, title, columns };
      })
    );

    try {
      const board = await boardApi.updateBoard(boardId, title, columnNames);
      replaceBoard(board);
    } catch (err) {
      showToast(`Couldn't save the board: ${err.message}`);
      await resync();
    }
  };

  const deleteBoard = async (boardId) => {
    const index = boards.findIndex((b) => b.id === boardId);
    if (index === -1) return;
    const remainingCount = boards.length - 1;

    setBoards((prev) => prev.filter((b) => b.id !== boardId));
    setActiveBoardIndex((prevIndex) => {
      if (remainingCount <= 0) return 0;
      if (index < prevIndex) return prevIndex - 1;
      if (index === prevIndex) return Math.min(prevIndex, remainingCount - 1);
      return prevIndex;
    });

    try {
      await boardApi.deleteBoard(boardId);
    } catch (err) {
      showToast(`Couldn't delete the board: ${err.message}`);
      await resync();
    }
  };

  const createNewColumn = async (boardId, name) => {
    const optimisticId = tempId();
    setBoards((prev) =>
      prev.map((board) =>
        board.id === boardId
          ? { ...board, columns: [...board.columns, { id: optimisticId, name, tasks: [] }] }
          : board
      )
    );

    try {
      const board = await boardApi.createColumn(boardId, name);
      replaceBoard(board);
    } catch (err) {
      showToast(`Couldn't create the column: ${err.message}`);
      await resync();
    }
  };

  const deleteColumn = async (boardId, columnId) => {
    setBoards((prev) =>
      prev.map((board) =>
        board.id === boardId
          ? { ...board, columns: board.columns.filter((col) => col.id !== columnId) }
          : board
      )
    );

    try {
      await boardApi.deleteColumn(columnId);
    } catch (err) {
      showToast(`Couldn't delete the column: ${err.message}`);
      await resync();
    }
  };

  const reorderColumns = async (boardId, sourceIndex, destinationIndex) => {
    const board = boards.find((b) => b.id === boardId);
    if (!board) return;

    const columns = [...board.columns];
    const [moved] = columns.splice(sourceIndex, 1);
    columns.splice(destinationIndex, 0, moved);
    setBoards((prev) => prev.map((b) => (b.id === boardId ? { ...b, columns } : b)));

    try {
      const updated = await boardApi.reorderColumns(boardId, columns.map((c) => c.id));
      replaceBoard(updated);
    } catch (err) {
      showToast(`Couldn't reorder the columns: ${err.message}`);
      await resync();
    }
  };

  const addNewTask = async (boardId, columnId, { title, description = "", subTasks = [] }) => {
    const newTask = { id: tempId(), title, description, subTasks };

    setBoards((prev) =>
      prev.map((board) =>
        board.id !== boardId
          ? board
          : {
              ...board,
              columns: board.columns.map((col) =>
                col.id === columnId ? { ...col, tasks: [...col.tasks, newTask] } : col
              ),
            }
      )
    );

    try {
      const board = await boardApi.createTask(columnId, { title, description, subTasks });
      replaceBoard(board);
    } catch (err) {
      showToast(`Couldn't create the task: ${err.message}`);
      await resync();
    }
  };

  // Updates a task's fields and/or moves it to a different column (used by
  // both EditTask's form and ViewTask's status dropdown). Field updates and
  // column moves are separate API calls since the backend keeps them separate.
  const editTask = async (boardId, taskId, updates = {}, newColumnId) => {
    const hasFieldUpdates =
      updates.title !== undefined || updates.description !== undefined || updates.subTasks !== undefined;

    const board = boards.find((b) => b.id === boardId);
    const sourceColumn = board?.columns.find((col) => col.tasks.some((t) => t.id === taskId));
    const sourceColumnId = sourceColumn?.id ?? null;
    const isMove = Boolean(newColumnId) && newColumnId !== sourceColumnId;
    const destColumn = board?.columns.find((col) => col.id === newColumnId);
    const destPosition = destColumn ? destColumn.tasks.length : 0;

    setBoards((prev) =>
      prev.map((b) => {
        if (b.id !== boardId) return b;

        let movedTask = null;
        let columns = b.columns.map((col) => {
          const task = col.tasks.find((t) => t.id === taskId);
          if (!task) return col;
          movedTask = { ...task, ...updates };
          if (isMove && col.id === sourceColumnId) {
            return { ...col, tasks: col.tasks.filter((t) => t.id !== taskId) };
          }
          return { ...col, tasks: col.tasks.map((t) => (t.id === taskId ? movedTask : t)) };
        });

        if (movedTask && isMove) {
          columns = columns.map((col) =>
            col.id === newColumnId ? { ...col, tasks: [...col.tasks, movedTask] } : col
          );
        }

        return { ...b, columns };
      })
    );

    try {
      let updatedBoard;
      if (hasFieldUpdates) {
        updatedBoard = await boardApi.updateTask(taskId, updates);
      }
      if (isMove) {
        updatedBoard = await boardApi.moveTask(taskId, newColumnId, destPosition);
      }
      if (updatedBoard) replaceBoard(updatedBoard);
    } catch (err) {
      showToast(`Couldn't save the task: ${err.message}`);
      await resync();
    }
  };

  const deleteTask = async (boardId, columnId, taskId) => {
    setBoards((prev) =>
      prev.map((board) =>
        board.id !== boardId
          ? board
          : {
              ...board,
              columns: board.columns.map((col) =>
                col.id === columnId
                  ? { ...col, tasks: col.tasks.filter((t) => t.id !== taskId) }
                  : col
              ),
            }
      )
    );

    try {
      await boardApi.deleteTask(taskId);
    } catch (err) {
      showToast(`Couldn't delete the task: ${err.message}`);
      await resync();
    }
  };

  const toggleSubtask = async (boardId, columnId, taskId, subtaskId) => {
    setBoards((prev) =>
      prev.map((board) =>
        board.id !== boardId
          ? board
          : {
              ...board,
              columns: board.columns.map((col) =>
                col.id !== columnId
                  ? col
                  : {
                      ...col,
                      tasks: col.tasks.map((t) =>
                        t.id !== taskId
                          ? t
                          : {
                              ...t,
                              subTasks: t.subTasks.map((st) =>
                                st.id === subtaskId ? { ...st, isCompleted: !st.isCompleted } : st
                              ),
                            }
                      ),
                    }
              ),
            }
      )
    );

    try {
      const board = await boardApi.toggleSubtask(subtaskId);
      replaceBoard(board);
    } catch (err) {
      showToast(`Couldn't update the subtask: ${err.message}`);
      await resync();
    }
  };

  // Moves a task to a specific index within a (possibly different) column.
  // Used by drag-and-drop, which needs precise position control.
  const moveTask = async (boardId, taskId, sourceColumnId, destinationColumnId, destinationIndex) => {
    setBoards((prev) =>
      prev.map((board) => {
        if (board.id !== boardId) return board;

        let movedTask = null;
        let columns = board.columns.map((col) => {
          const task = col.tasks.find((t) => t.id === taskId);
          if (!task) return col;
          movedTask = task;
          return { ...col, tasks: col.tasks.filter((t) => t.id !== taskId) };
        });

        columns = columns.map((col) => {
          if (col.id !== destinationColumnId || !movedTask) return col;
          const tasks = [...col.tasks];
          tasks.splice(destinationIndex, 0, movedTask);
          return { ...col, tasks };
        });

        return { ...board, columns };
      })
    );

    try {
      const board = await boardApi.moveTask(taskId, destinationColumnId, destinationIndex);
      replaceBoard(board);
    } catch (err) {
      showToast(`Couldn't move the task: ${err.message}`);
      await resync();
    }
  };

  return (
    <BoardContext.Provider
      value={{
        boards,
        activeBoardIndex,
        sidebarOpen,
        setSidebarOpen,
        isLoading,
        error,
        createNewBoard,
        selectBoard,
        editBoard,
        deleteBoard,
        createNewColumn,
        deleteColumn,
        reorderColumns,
        addNewTask,
        editTask,
        deleteTask,
        toggleSubtask,
        moveTask,
      }}
    >
      {children}
    </BoardContext.Provider>
  );
};
